import { useCallback, useEffect, useState } from 'react';
import { IoAddOutline } from 'react-icons/io5';
import { challengeApi } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import { AdminModal, AdminField, AdminCheck, ConfirmModal } from '../../components/AdminModal';
import { apiErrorMessage } from '../../utils/apiError';
import AdminDataTable, { AdminPill } from '../../components/admin/AdminDataTable';
import BulkActionHost from '../../components/admin/BulkActionHost';
import {
  useBulkAction, useRowSelection, runBulk, summarizeBulk, useFirstLoad
} from '../../components/admin/useAdminTable';
import './AdminChallengesPage.css';

const GOAL_KEYS = [
  { key: 'bible_completions', label: 'Complete read-throughs', unit: 'read-throughs' },
  { key: 'chapters_read', label: 'Chapters read together', unit: 'chapters' },
  { key: 'chapters_covered', label: 'Chapters of the Bible reached', unit: 'chapters' }
];

const blankChallenge = () => ({
  name: '',
  slug: '',
  startDate: '',
  endDate: '',
  description: '',
  isActive: true
});

const asDateOnly = (value) => (value ? String(value).split('T')[0] : '');

const ChallengeFormModal = ({ challenge, onClose, onSaved }) => {
  const [form, setForm] = useState(challenge ? {
    name: challenge.name,
    slug: challenge.slug,
    startDate: asDateOnly(challenge.startDate),
    endDate: asDateOnly(challenge.endDate),
    description: challenge.description || '',
    isActive: challenge.isActive
  } : blankChallenge());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.endDate < form.startDate) {
      setError('End date must not be before start date.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (challenge) {
        await challengeApi.updateChallenge(challenge.id, form);
        onSaved(`Saved "${form.name}".`);
      } else {
        await challengeApi.createChallenge(form);
        onSaved(`Created "${form.name}".`);
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save challenge.'));
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title={challenge ? `Edit ${challenge.name}` : 'New challenge'}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="challenge-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Saving…' : challenge ? 'Save changes' : 'Create challenge'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <form id="challenge-form" onSubmit={handleSubmit}>
        <AdminField label="Name">
          <input
            type="text"
            value={form.name}
            onChange={set('name')}
            placeholder="Reformation Reading 2026"
            required
            maxLength={200}
          />
        </AdminField>
        <AdminField label="Slug" hint="Lowercase letters, numbers and hyphens.">
          <input
            type="text"
            value={form.slug}
            onChange={set('slug')}
            placeholder="reformation-reading-2026"
            pattern="[a-z0-9-]+"
            title="Lowercase letters, numbers and hyphens"
            required
          />
        </AdminField>
        <div className="challenge-date-row">
          <AdminField label="Starts">
            <input type="date" value={form.startDate} onChange={set('startDate')} required />
          </AdminField>
          <AdminField label="Ends">
            <input type="date" value={form.endDate} onChange={set('endDate')} required />
          </AdminField>
        </div>
        <AdminField label="Description">
          <textarea
            value={form.description}
            onChange={set('description')}
            rows={2}
            maxLength={2000}
            placeholder="Read the Bible through together during October."
          />
        </AdminField>
        <AdminCheck
          label="Active"
          hint="It runs only while active and today is inside its window."
          checked={form.isActive}
          onChange={set('isActive')}
        />
      </form>
    </AdminModal>
  );
};

/**
 * The three global goal targets for one challenge, each saved or cleared on
 * its own, with its own status line so a failure shows next to the field.
 */
const ChallengeGoalsEditor = ({ challenge, onChanged }) => {
  const globalGoals = (challenge.goals || []).filter((goal) => !goal.churchId);
  const [drafts, setDrafts] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [status, setStatus] = useState({});

  const report = (key, tone, text) => setStatus((prev) => ({ ...prev, [key]: { tone, text } }));

  const save = async (meta) => {
    const targetValue = parseInt(drafts[meta.key], 10);
    if (Number.isNaN(targetValue) || targetValue < 0) {
      report(meta.key, 'error', 'Target must be a number of 0 or more.');
      return;
    }
    try {
      setBusyKey(meta.key);
      await challengeApi.saveGoal(challenge.id, {
        key: meta.key,
        targetValue,
        name: meta.label,
        unit: meta.unit,
        displayOrder: GOAL_KEYS.findIndex((goal) => goal.key === meta.key) + 1
      });
      setDrafts((prev) => ({ ...prev, [meta.key]: undefined }));
      report(meta.key, 'ok', 'Saved.');
      onChanged();
    } catch (err) {
      report(meta.key, 'error', apiErrorMessage(err, 'Failed to save this target.'));
    } finally {
      setBusyKey(null);
    }
  };

  const clear = async (meta, goal) => {
    try {
      setBusyKey(meta.key);
      await challengeApi.deleteGoal(goal.id);
      setDrafts((prev) => ({ ...prev, [meta.key]: undefined }));
      report(meta.key, 'ok', 'Target removed.');
      onChanged();
    } catch (err) {
      report(meta.key, 'error', apiErrorMessage(err, 'Failed to remove this target.'));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="challenge-goals-editor">
      <p className="challenge-goals-hint">
        Site-wide targets for this challenge. Church-specific targets are not edited here.
      </p>
      {GOAL_KEYS.map((meta) => {
        const existing = globalGoals.find((goal) => goal.key === meta.key);
        const saved = existing ? String(existing.targetValue) : '';
        const value = drafts[meta.key] ?? saved;
        const busy = busyKey === meta.key;
        const line = status[meta.key];
        return (
          <div key={meta.key} className="challenge-goal-row">
            <label className="goal-label" htmlFor={`goal-${challenge.id}-${meta.key}`}>{meta.label}</label>
            <input
              id={`goal-${challenge.id}-${meta.key}`}
              type="number"
              min="0"
              value={value}
              placeholder="no target"
              onChange={(event) => setDrafts((prev) => ({ ...prev, [meta.key]: event.target.value }))}
            />
            <button type="button" onClick={() => save(meta)} disabled={busy || value === '' || value === saved}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            {existing && (
              <button type="button" className="is-secondary" onClick={() => clear(meta, existing)} disabled={busy}>
                Clear
              </button>
            )}
            {line && <span className={`challenge-goal-status is-${line.tone}`} role="status">{line.text}</span>}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Create and edit challenges and their global goals (WS4).
 *
 * A challenge is only "running" when it is active AND today falls inside its
 * window, which is what the home hero keys off — so this page shows that
 * status explicitly rather than just the isActive flag.
 */
const AdminChallengesPage = () => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null); // a challenge, or 'new'
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const firstLoad = useFirstLoad(loading);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await challengeApi.getAllForAdmin();
      setChallenges(response.data.challenges || []);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load challenges.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Local calendar day — UTC toISOString() flips the badge after evening in the US.
  const today = new Intl.DateTimeFormat('en-CA').format(new Date());
  const isRunning = (c) => c.isActive && asDateOnly(c.startDate) <= today && today <= asDateOnly(c.endDate);

  const handleSaved = (text) => {
    setEditing(null);
    setMessage(text);
    load();
  };

  const closeDelete = () => {
    setDeleting(null);
    setDeleteError(null);
  };

  const remove = async () => {
    try {
      setDeleteBusy(true);
      setDeleteError(null);
      await challengeApi.deleteChallenge(deleting.id);
      setMessage(`Deleted "${deleting.name}".`);
      closeDelete();
      load();
    } catch (err) {
      setDeleteError(apiErrorMessage(err, 'Failed to delete challenge.'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const selection = useRowSelection(challenges.map((challenge) => challenge.id).join(','));
  const bulk = useBulkAction((_result, text) => {
    selection.clear();
    setMessage(text);
    load();
  });

  if (firstLoad) return <LoadingSpinner size="large" message="Loading challenges..." />;

  return (
    <div className="admin-challenges">
      <div className="admin-page-header admin-challenges-header">
        <div>
          <h1 className="admin-page-title">Challenges</h1>
          <p className="admin-page-description">
            A challenge gives the community a season with a start, an end, and a countdown.
            Only one runs at a time: the one that is active and whose window contains today.
          </p>
        </div>
        <button type="button" className="challenge-new-btn" onClick={() => setEditing('new')}>
          <IoAddOutline /> New challenge
        </button>
      </div>

      {message && (
        <div className="admin-challenges-message" role="status">
          <span>{message}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setMessage('')}>&times;</button>
        </div>
      )}

      <AdminDataTable
        title={`Challenges (${challenges.length})`}
        rows={challenges}
        getRowLabel={(challenge) => challenge.name}
        selection={selection}
        loading={loading}
        error={error}
        onRetry={load}
        emptyTitle="No challenges yet"
        emptyMessage="Create one to give the community a season."
        expandedId={expandedId}
        onToggleExpand={(id) => setExpandedId(expandedId === id ? null : id)}
        renderExpanded={(challenge) => <ChallengeGoalsEditor challenge={challenge} onChanged={load} />}
        bulkActions={[
          {
            label: 'Delete selected',
            danger: true,
            onClick: (rows) => bulk.ask({
              title: `Delete ${rows.length} challenge${rows.length === 1 ? '' : 's'}`,
              confirmLabel: 'Delete challenges',
              body: <p className="admin-modal-hint">The challenges and their goals go with them. Reading reports are not affected.</p>,
              run: () => runBulk(rows, (row) => challengeApi.deleteChallenge(row.id)),
              summarize: (result) => summarizeBulk('Deleted', 'challenge', result)
            })
          }
        ]}
        columns={[
          { key: 'name', header: 'Name', render: (challenge) => <span className="admin-cell-strong">{challenge.name}</span> },
          {
            key: 'window',
            header: 'Window',
            render: (challenge) => `${asDateOnly(challenge.startDate)} → ${asDateOnly(challenge.endDate)}`
          },
          { key: 'slug', header: 'Slug', render: (challenge) => <code>{challenge.slug}</code> },
          {
            key: 'status',
            header: 'Status',
            render: (challenge) => {
              if (isRunning(challenge)) return <AdminPill tone="ok">Running now</AdminPill>;
              if (!challenge.isActive) return <AdminPill tone="danger">Inactive</AdminPill>;
              return <AdminPill tone="info">{asDateOnly(challenge.startDate) > today ? 'Scheduled' : 'Ended'}</AdminPill>;
            }
          }
        ]}
        renderActions={(challenge) => (
          <>
            <button type="button" onClick={() => setEditing(challenge)}>Edit</button>
            <button type="button" className="is-danger" onClick={() => setDeleting(challenge)}>Delete</button>
          </>
        )}
      />
      <BulkActionHost bulk={bulk} />

      {editing && (
        <ChallengeFormModal
          challenge={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={`Delete ${deleting.name}`}
          confirmLabel="Delete challenge"
          busy={deleteBusy}
          error={deleteError}
          onCancel={closeDelete}
          onConfirm={remove}
        >
          <p className="admin-modal-hint">
            The challenge and its goals go with it. Reading reports are not affected.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default AdminChallengesPage;
